import React from 'react';
import { motion } from 'framer-motion';
import { FiSettings, FiMail } from 'react-icons/fi';

const CookiesPolicy = () => {
  const sections = [
    {
      id: 1,
      title: "What Are Cookies?",
      content: (
        <>
          <p className="text-gray-400 mb-3">1.1 Cookies are small text files that are stored on your device (computer, tablet, or mobile) when you visit a website.</p>
          <p className="text-gray-400 mb-3">1.2 Cookies help websites recognize your device and remember information about your visit, such as your preferences and settings.</p>
          <p className="text-gray-400">1.3 We also use similar technologies such as pixels, web beacons, and local storage to collect and store information.</p>
        </>
      )
    },
    {
      id: 2,
      title: "Types of Cookies We Use",
      content: (
        <>
          <h4 className="text-lg font-semibold text-white mb-3">2.1 Essential Cookies</h4>
          <p className="text-gray-400 mb-4">These cookies are strictly necessary for the Service to function. They enable core functionality such as security, authentication, and session management. You cannot opt out of these cookies.</p>

          <h4 className="text-lg font-semibold text-white mb-3">2.2 Performance & Analytics Cookies</h4>
          <p className="text-gray-400 mb-4">These cookies collect anonymous information about how you use the Service, including pages visited, time spent, and error messages. This helps us improve platform performance and user experience.</p>

          <h4 className="text-lg font-semibold text-white mb-3">2.3 Functional Cookies</h4>
          <p className="text-gray-400 mb-4">These cookies remember your preferences and settings (such as language, region, or display options) to provide a more personalized experience.</p>

          <h4 className="text-lg font-semibold text-white mb-3">2.4 Marketing & Targeting Cookies</h4>
          <p className="text-gray-400">These cookies may be used to deliver relevant advertisements and track the effectiveness of our marketing campaigns. We currently do not use third-party advertising cookies, but we may use analytics for remarketing purposes.</p>
        </>
      )
    },
    {
      id: 3,
      title: "How We Use Cookies",
      content: (
        <>
          <p className="text-gray-400 mb-4">We use cookies and similar technologies to:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Authenticate users and maintain secure sessions</li>
            <li>Remember your preferences and settings</li>
            <li>Analyze platform usage and performance</li>
            <li>Improve our AI models and user experience</li>
            <li>Detect and prevent fraud or unauthorized access</li>
            <li>Comply with legal and regulatory requirements</li>
          </ul>
        </>
      )
    },
    {
      id: 4,
      title: "Third-Party Cookies",
      content: (
        <>
          <p className="text-gray-400 mb-4">We may use third-party services that set their own cookies, including:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4 mb-4">
            <li><span className="text-cyan-400">Analytics providers</span> – to understand how users interact with the Service</li>
            <li><span className="text-cyan-400">Payment processors</span> – to securely process transactions (e.g., Stripe)</li>
            <li><span className="text-cyan-400">Authentication services</span> – to enable secure login functionality</li>
            <li><span className="text-cyan-400">Cloud infrastructure</span> – to deliver and optimize content</li>
          </ul>
          <p className="text-gray-400">These third parties have their own privacy and cookie policies. We encourage you to review their policies.</p>
        </>
      )
    },
    {
      id: 5,
      title: "Cookie Duration",
      content: (
        <>
          <h4 className="text-lg font-semibold text-white mb-3">5.1 Session Cookies</h4>
          <p className="text-gray-400 mb-4">These cookies are temporary and are deleted when you close your browser.</p>

          <h4 className="text-lg font-semibold text-white mb-3">5.2 Persistent Cookies</h4>
          <p className="text-gray-400">These cookies remain on your device for a set period (ranging from days to years) or until you delete them manually. They help us remember your preferences across visits.</p>
        </>
      )
    },
    {
      id: 6,
      title: "Managing Your Cookie Preferences",
      content: (
        <>
          <p className="text-gray-400 mb-4">You can control and manage cookies in several ways:</p>
          
          <h4 className="text-lg font-semibold text-white mb-3">6.1 Browser Settings</h4>
          <p className="text-gray-400 mb-4">Most browsers allow you to view, manage, and delete cookies through their settings. Please refer to your browser's help documentation for instructions.</p>

          <h4 className="text-lg font-semibold text-white mb-3">6.2 Opt-Out Tools</h4>
          <p className="text-gray-400 mb-4">You can opt out of certain analytics and advertising cookies using industry tools such as the Digital Advertising Alliance (DAA) or Network Advertising Initiative (NAI) opt-out pages.</p>

          <div className="p-4 bg-gradient-to-r from-orange-600/10 to-yellow-600/10 border border-orange-500/20 rounded-xl">
            <p className="text-orange-300 text-sm">
              <strong>Note:</strong> Disabling essential cookies may prevent you from using certain features of the Service, including logging in or accessing your account.
            </p>
          </div>
        </>
      )
    },
    {
      id: 7,
      title: "Do Not Track Signals",
      content: (
        <p className="text-gray-400">Some browsers offer a "Do Not Track" (DNT) feature. Currently, the Service does not respond to DNT signals, but we respect your privacy choices through our cookie management options.</p>
      )
    },
    {
      id: 8,
      title: "Data Protection & Security",
      content: (
        <>
          <p className="text-gray-400 mb-4">We apply enterprise-grade protection to all data collected through cookies:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>AES-256 data encryption</li>
            <li>TLS 1.3 secure communication</li>
            <li>Role-based access control (RBAC)</li>
            <li>Continuous monitoring</li>
            <li>Zero-trust architecture</li>
          </ul>
        </>
      )
    },
    {
      id: 9,
      title: "International Data Transfers",
      content: (
        <>
          <p className="text-gray-400 mb-4">Cookie data may be processed in:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4 mb-4">
            <li>Singapore</li>
            <li>Panama</li>
            <li>United States</li>
            <li>European Union</li>
            <li>Other cloud regions</li>
          </ul>
          <p className="text-gray-400">All transfers comply with GDPR-approved mechanisms (SCCs), PDPA, and global privacy frameworks.</p>
        </>
      )
    },
    {
      id: 10,
      title: "Your Rights",
      content: (
        <>
          <p className="text-gray-400 mb-4">Depending on your jurisdiction, you may have the right to:</p>
          <ul className="list-disc list-inside text-gray-400 space-y-2 ml-4">
            <li>Access information about cookies we use</li>
            <li>Withdraw consent for non-essential cookies</li>
            <li>Request deletion of cookie-related data</li>
            <li>Object to certain types of data processing</li>
          </ul>
        </>
      )
    },
    {
      id: 11,
      title: "Updates to This Policy",
      content: (
        <>
          <p className="text-gray-400 mb-2">We may update this Cookie Policy from time to time to reflect changes in our practices or applicable laws.</p>
          <p className="text-gray-400">When updated, the "Last Updated" date will change. Continued use of the Service indicates acceptance of the updated policy.</p>
        </>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white">
      {/* Hero Section */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-600/10 to-cyan-600/10"></div>
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[400px] bg-gradient-to-r from-purple-600/20 to-cyan-600/20 blur-3xl rounded-full"></div>
        
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="p-3 rounded-xl bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30">
                <FiSettings className="text-3xl text-cyan-400" />
              </div>
            </div>
            <h1 className="text-4xl md:text-5xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                COOKIE POLICY
              </span>
            </h1>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 text-gray-400 text-sm">
              <span>Effective Date: December 6, 2025</span>
              <span className="hidden sm:block">•</span>
              <span>Last Updated: December 6, 2025</span>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Introduction */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl"
        >
          <p className="text-gray-300 leading-relaxed mb-4">
            This Cookie Policy explains how <span className="text-cyan-400 font-medium">Quantum AI FinTech Pte. Ltd.</span> (Singapore Parent) and <span className="text-cyan-400 font-medium">Quantum AI FinTech Corporation SA</span> (Panama Operations Entity) use cookies and similar tracking technologies when you access or use <span className="text-cyan-400 font-medium">SnapChart AI</span>, <span className="text-cyan-400 font-medium">TitanVue AI</span>, or any related products and services (the "Service").
          </p>
          <p className="text-gray-400">
            By continuing to use the Service, you consent to the use of cookies as described in this policy.
          </p>
        </motion.div>
      </div>

      {/* Sections */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-12">
        <div className="space-y-8">
          {sections.map((section, index) => (
            <motion.div
              key={section.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 * index }}
              className="p-6 bg-gray-800/30 border border-white/5 rounded-2xl hover:border-purple-500/20 transition-colors"
            >
              <h3 className="text-xl font-bold mb-4 flex items-center gap-3">
                <span className="flex items-center justify-center w-8 h-8 rounded-lg bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 text-cyan-400 text-sm font-bold">
                  {section.id}
                </span>
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  {section.title}
                </span>
              </h3>
              <div className="pl-11">
                {section.content}
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Contact Section */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="p-6 bg-gradient-to-r from-purple-600/10 to-cyan-600/10 border border-purple-500/20 rounded-2xl text-center"
        >
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
            Contact Information
          </h3>
          <p className="text-gray-400 mb-4">For questions regarding cookies or this policy, contact:</p>
          <a href="mailto:support@qfintech.ai" className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-purple-600/20 to-cyan-600/20 border border-purple-500/30 rounded-xl text-cyan-400 hover:text-cyan-300 hover:border-purple-500/50 transition-all font-medium">
            <FiMail className="text-lg" />
            support@qfintech.ai
          </a>
        </motion.div>
      </div>
    </div>
  );
};

export default CookiesPolicy;
